import { useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import DataTable from '../../../components/tables/DataTable';
import SimpleDropdown from '../../../components/SimpleDropdown';
import {
	listLoyaltyPrograms,
	listStoreLoyaltyPrograms,
	addStoreLoyaltyProgram,
	removeStoreLoyaltyProgram,
} from '../../../utils/queries';

export default function LoyaltyProgramsTab({ storeId, onChange, canEdit = true }) {
	const [catalog, setCatalog] = useState([]);
	const [assigned, setAssigned] = useState([]);
	const [loading, setLoading] = useState(true);
	const [selected, setSelected] = useState(null);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');

	const refresh = () => {
		setLoading(true);
		return Promise.all([listLoyaltyPrograms(), listStoreLoyaltyPrograms(storeId)]).then(
			([catalogResult, assignedResult]) => {
				if (catalogResult?.success) setCatalog(catalogResult.data);
				if (assignedResult?.success) {
					setAssigned(assignedResult.data);
					onChange?.(assignedResult.data);
				}
				setLoading(false);
			}
		);
	};

	useEffect(() => {
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [storeId]);

	const programName = (loyaltyProgramId) =>
		catalog.find((p) => p.loyaltyProgramId === loyaltyProgramId)?.programName || loyaltyProgramId;

	const available = catalog.filter(
		(p) => !assigned.some((a) => a.loyaltyProgramId === p.loyaltyProgramId)
	);

	useEffect(() => {
		if (!available.some((p) => p.loyaltyProgramId === selected)) {
			setSelected(available[0]?.loyaltyProgramId ?? null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [available.length]);

	const handleAdd = async (e) => {
		e.preventDefault();
		if (!selected) return;
		setSaving(true);
		setError('');
		const result = await addStoreLoyaltyProgram(storeId, selected);
		setSaving(false);
		if (!result?.success) {
			setError(result?.error?.message || 'Failed to add.');
			return;
		}
		setSelected(null);
		refresh();
	};

	const handleRemove = async (id) => {
		await removeStoreLoyaltyProgram(id);
		refresh();
	};

	const columns = canEdit
		? [
				{ key: 'loyaltyProgramId', header: 'Program', render: (id) => programName(id) },
				{
					key: 'storeLoyaltyProgramId',
					header: '',
					sortable: false,
					render: (id) => (
						<button type='button' onClick={() => handleRemove(id)}>
							<Trash2 size={20} />
						</button>
					),
				},
			]
		: [{ key: 'loyaltyProgramId', header: 'Program', render: (id) => programName(id) }];

	return (
		<div className='max-w-2xl'>
			<DataTable columns={columns} data={assigned} loading={loading} emptyHeading='No loyalty programs assigned yet' />

			{canEdit && !loading && available.length > 0 && (
				<form onSubmit={handleAdd} className='flex items-end gap-3 mt-5'>
					<div className='flex flex-col'>
						<label>Loyalty Program</label>
						<SimpleDropdown
							value={selected}
							onChange={setSelected}
							options={available.map((p) => ({ value: p.loyaltyProgramId, label: p.programName }))}
							className='w-64'
						/>
					</div>
					{error && <div className='text-sm text-pi-red'>{error}</div>}
					<button type='submit' disabled={saving || !selected} className='btn-primary h-10'>
						{saving ? 'Adding...' : 'Add'}
					</button>
				</form>
			)}
		</div>
	);
}
